/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useInfiniteQuery, UseInfiniteQueryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TCase } from './types/case.type';
// import { TCase } from '../types/case.type';

/**
 *
 */
export type TGetGroupMessagesResponse = ApiResponse<TCase[]>;

export interface GetGroupMessagesParams {
  groupInfoId?: string;
  messageOrder?: string;
  limit?: number | string;
}

export const getGroupMessages = async (params: GetGroupMessagesParams): Promise<TGetGroupMessagesResponse> => {
  const url = `/chat/get-group-messages`;

  return axiosInstance.get(url, { params });
};

/**
 * Use Get Group Messages React-Query Hook
 */
export const GET_GROUP_MESSAGES_QUERY_KEY = 'group-messages';

export const getGroupMessagesQueryOptions = (params: GetGroupMessagesParams) => {
  return queryOptions({
    queryKey: [GET_GROUP_MESSAGES_QUERY_KEY, params?.groupInfoId, params?.messageOrder, params?.limit],
    queryFn: () => getGroupMessages(params),
    select: (data) => {
      const reversed = [...(data.payload || [])].reverse();
      return {
        ...data,
        payload: reversed,
      };
    },
  });
};

type UseGetGroupMessagesOptions = {
  params: GetGroupMessagesParams;
  queryConfig?: QueryConfig<typeof getGroupMessagesQueryOptions>;
};

export const useGetGroupMessages = ({ params, queryConfig }: UseGetGroupMessagesOptions) => {
  return useQuery({
    ...getGroupMessagesQueryOptions(params),
    ...queryConfig,
  });
};

/**
 * Use Get Infinite Group Messages React-Query Hook
 */
export const GET_INFINITE_GROUP_MESSAGES_QUERY_KEY = 'infinite-group-messages';
export const DEFAULT_INFINITE_GROUP_MESSAGES_LIMIT_PARAMS = 50;

type UseGetInfiniteGroupMessagesOptions = {
  params: GetGroupMessagesParams;
  queryConfig?: UseInfiniteQueryOptions<TGetGroupMessagesResponse, Error>;
};

export const useGetInfiniteGroupMessages = ({ params, queryConfig }: UseGetInfiniteGroupMessagesOptions) => {
  return useInfiniteQuery({
    queryKey: [GET_INFINITE_GROUP_MESSAGES_QUERY_KEY, params?.groupInfoId, params?.limit],
    queryFn: ({ pageParam = undefined }): Promise<TGetGroupMessagesResponse> =>
      getGroupMessages({
        groupInfoId: params?.groupInfoId,
        messageOrder: pageParam ? String(pageParam) : undefined,
        limit: params?.limit,
      }),
    getNextPageParam: (lastPage) => {
      const messages = lastPage?.payload;
      if (messages?.length > 0) {
        // return messages[0]?.messageOrder; // fetch older messages
        return messages[messages.length - 1]?.messageOrder; // fetch older messages using the last message's order
      }
      return undefined;
    },
    enabled: !!params?.groupInfoId,
    initialPageParam: '' as any,
    ...queryConfig,
  });
};
